import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { scoreColor } from "./ProjectScoreCard";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projectId: string;
  grant: any | null;
}

const GrantFitDialog = ({ open, onOpenChange, projectId, grant }: Props) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!open || !grant) return;
    setResult(null);
    setLoading(true);
    supabase.functions.invoke("score-project-against-grant", {
      body: { project_id: projectId, grant },
    }).then(({ data, error }) => {
      if (error) {
        toast.error(error.message ?? "Failed to score fit");
        onOpenChange(false);
      } else {
        setResult(data?.score);
      }
    }).finally(() => setLoading(false));
  }, [open, grant, projectId, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fit analysis: {grant?.title}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing fit…
          </div>
        ) : result ? (
          <div className="space-y-5">
            <div className="flex items-end gap-3">
              <div className={`font-display text-5xl font-bold ${scoreColor(result.score)}`}>{result.score}</div>
              <div className="pb-2 text-xs text-muted-foreground">/ 100 fit score</div>
            </div>
            <p className="text-sm text-muted-foreground">{result.summary}</p>

            {result.rubric?.strengths?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-emerald-400 mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Strengths
                </h4>
                <ul className="space-y-1.5">
                  {result.rubric.strengths.map((s: string, i: number) => (
                    <li key={i} className="text-sm text-muted-foreground">• {s}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.rubric?.gaps?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-amber-400 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Gaps
                </h4>
                <ul className="space-y-1.5">
                  {result.rubric.gaps.map((s: string, i: number) => (
                    <li key={i} className="text-sm text-muted-foreground">• {s}</li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(result.suggestions) && result.suggestions.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Tailored next steps</h4>
                <div className="space-y-2">
                  {result.suggestions.map((s: any, i: number) => (
                    <div key={i} className="border border-border rounded-lg p-3">
                      <div className="text-sm font-semibold mb-0.5">{s.title}</div>
                      <div className="text-xs text-muted-foreground">{s.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default GrantFitDialog;